/**
 * WorkTimelineView — Linha do tempo de trabalho unificado (modo análise).
 */
import React, { useMemo } from 'react';
import { CalendarRange } from 'lucide-react';
import type { UnifiedWorkItem } from '../types/workItem';
import { WORK_SOURCE_LABELS, WORK_SOURCE_COLORS, WORK_STATUS_LABELS, WORK_STATUS_COLORS } from '../types/workItem';

function formatDate(iso: string): string {
  const d = new Date(iso);
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);

  if (d.toDateString() === today.toDateString()) return 'Hoje';
  if (d.toDateString() === yesterday.toDateString()) return 'Ontem';

  return d.toLocaleDateString('pt-BR', { weekday: 'short', day: '2-digit', month: 'short' });
}

function formatDateShort(iso: string): string {
  return new Date(iso).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' });
}

function groupByDate(items: UnifiedWorkItem[]): Map<string, UnifiedWorkItem[]> {
  const groups = new Map<string, UnifiedWorkItem[]>();

  const sorted = [...items].sort((a, b) => {
    const da = a.due_date ?? a.created_at;
    const db = b.due_date ?? b.created_at;
    return new Date(da).getTime() - new Date(db).getTime();
  });

  for (const item of sorted) {
    const dateKey = (item.due_date ?? item.created_at).slice(0, 10);
    const list = groups.get(dateKey) ?? [];
    list.push(item);
    groups.set(dateKey, list);
  }

  return groups;
}

function TimelineMetric({ label, value, tone = 'default' }: { label: string; value: number | string; tone?: 'default' | 'danger' | 'muted' }) {
  const toneClass = {
    default: 'text-slate-800 dark:text-slate-200',
    danger: 'text-red-600 dark:text-red-400',
    muted: 'text-slate-500 dark:text-slate-400',
  }[tone];
  return (
    <div className="flex flex-col items-center rounded-sm border border-violet-200/70 bg-white/80 px-2.5 py-1 dark:border-violet-900/40 dark:bg-slate-900/40">
      <span className={`font-mono text-[14px] font-bold tabular-nums leading-none ${toneClass}`}>{value}</span>
      <span className="mt-0.5 text-[8px] font-bold uppercase tracking-wider text-slate-400">{label}</span>
    </div>
  );
}

interface WorkTimelineViewProps {
  items: UnifiedWorkItem[];
  onItemClick?: (sourceId: string) => void;
  filtersActive?: boolean;
}

export const WorkTimelineView: React.FC<WorkTimelineViewProps> = React.memo(({ items, onItemClick, filtersActive }) => {
  const grouped = useMemo(() => groupByDate(items), [items]);

  const summary = useMemo(() => {
    const active = items.filter((i) => i.status !== 'completed');
    const overdue = active.filter((i) => i.is_overdue).length;
    const dates = items.map((i) => i.due_date ?? i.created_at).filter(Boolean).sort();
    const span = dates.length >= 2
      ? `${formatDateShort(dates[0])} – ${formatDateShort(dates[dates.length - 1])}`
      : dates.length === 1
        ? formatDateShort(dates[0])
        : '—';
    return { total: items.length, overdue, groups: grouped.size, span };
  }, [items, grouped.size]);

  if (items.length === 0) {
    return (
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden bg-gradient-to-b from-violet-50/50 to-[var(--bg-workspace)] dark:from-violet-950/20">
        <header className="shrink-0 border-b border-violet-200/80 bg-violet-50/90 px-4 py-2.5 dark:border-violet-900/35 dark:bg-violet-950/30">
          <div className="flex items-center gap-2.5">
            <div className="flex h-9 w-9 items-center justify-center rounded-sm border border-violet-300/60 bg-white text-violet-600 dark:border-violet-800 dark:bg-violet-950/50 dark:text-violet-400">
              <CalendarRange size={18} />
            </div>
            <div>
              <p className="font-mono text-[11px] font-bold uppercase tracking-[0.22em] text-violet-700 dark:text-violet-400">
                Linha do Tempo
              </p>
              <p className="text-[10px] text-slate-600 dark:text-slate-400">Análise cronológica · agrupamento por data</p>
            </div>
          </div>
        </header>
        <div className="flex flex-1 items-center justify-center text-sm text-slate-400 dark:text-slate-500">
          Nenhum item de trabalho encontrado{filtersActive ? ' com os filtros atuais' : ''}
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden bg-gradient-to-b from-violet-50/50 to-[var(--bg-workspace)] dark:from-violet-950/20">
      <header className="shrink-0 border-b border-violet-200/80 bg-violet-50/90 px-4 py-2.5 dark:border-violet-900/35 dark:bg-violet-950/30">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2.5">
            <div className="flex h-9 w-9 items-center justify-center rounded-sm border border-violet-300/60 bg-white text-violet-600 dark:border-violet-800 dark:bg-violet-950/50 dark:text-violet-400">
              <CalendarRange size={18} />
            </div>
            <div>
              <p className="font-mono text-[11px] font-bold uppercase tracking-[0.22em] text-violet-700 dark:text-violet-400">
                Linha do Tempo
              </p>
              <p className="text-[10px] text-slate-600 dark:text-slate-400">
                Análise cronológica · {summary.groups} {summary.groups === 1 ? 'dia' : 'dias'}
                {filtersActive ? ' · filtros ativos' : ''}
              </p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-1.5">
            <TimelineMetric label="Eventos" value={summary.total} />
            <TimelineMetric label="Atrasados" value={summary.overdue} tone={summary.overdue > 0 ? 'danger' : 'muted'} />
            <TimelineMetric label="Período" value={summary.span} tone="muted" />
          </div>
        </div>
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3 space-y-4">
        {Array.from(grouped.entries()).map(([dateKey, dateItems]) => (
          <div key={dateKey}>
            <div className="sticky top-0 z-10 mb-2 flex items-center gap-2 bg-[var(--bg-workspace)] py-1">
              <div className="h-px flex-1 bg-violet-200 dark:bg-violet-800/50" />
              <span className="rounded-sm bg-violet-100 px-2 py-0.5 font-mono text-[9px] font-bold uppercase tracking-wider text-violet-700 dark:bg-violet-950/40 dark:text-violet-300">
                {formatDate(dateKey)} · {dateItems.length}
              </span>
              <div className="h-px flex-1 bg-violet-200 dark:bg-violet-800/50" />
            </div>

            <div className="space-y-1.5 border-l-2 border-violet-200 pl-4 ml-2 dark:border-violet-800/60">
              {dateItems.map((item) => {
                const daysUntilDue = item.due_date
                  ? Math.ceil((new Date(item.due_date).getTime() - Date.now()) / (1000 * 60 * 60 * 24))
                  : null;

                return (
                  <div
                    key={item.id}
                    role={onItemClick ? 'button' : undefined}
                    tabIndex={onItemClick ? 0 : undefined}
                    onClick={onItemClick ? () => onItemClick(item.source_id) : undefined}
                    onKeyDown={onItemClick ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onItemClick(item.source_id); } } : undefined}
                    className={`relative flex items-start gap-3 rounded-md border border-slate-200 bg-white p-2.5 dark:border-slate-700 dark:bg-slate-800/60 ${onItemClick ? 'cursor-pointer transition-colors hover:border-violet-300 hover:bg-violet-50/50 dark:hover:border-violet-700 dark:hover:bg-violet-950/20' : ''}`}
                  >
                    <div className="absolute -left-[calc(1rem+5px)] top-3 h-2 w-2 rounded-full bg-violet-400 dark:bg-violet-500" />

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className={`rounded px-1.5 py-0.5 text-[9px] font-semibold ${WORK_SOURCE_COLORS[item.source]}`}>
                          {WORK_SOURCE_LABELS[item.source]}
                        </span>
                        <span className={`rounded px-1.5 py-0.5 text-[9px] font-medium ${WORK_STATUS_COLORS[item.status]}`}>
                          {WORK_STATUS_LABELS[item.status]}
                        </span>
                        {item.is_overdue && (
                          <span className="rounded bg-red-100 px-1.5 py-0.5 text-[9px] font-bold text-red-700 dark:bg-red-900/30 dark:text-red-400">
                            ATRASADO
                          </span>
                        )}
                      </div>
                      <p className="text-[11px] font-medium text-slate-800 dark:text-slate-200 truncate">
                        {item.title}
                      </p>
                      <div className="flex items-center gap-3 mt-1 text-[9px] text-slate-500 dark:text-slate-400">
                        {item.priority && (
                          <span className="font-medium uppercase">{item.priority}</span>
                        )}
                        {daysUntilDue !== null && item.status !== 'completed' && (
                          <span className={daysUntilDue < 0 ? 'text-red-500 font-semibold' : ''}>
                            {daysUntilDue < 0
                              ? `${Math.abs(daysUntilDue)}d atraso`
                              : daysUntilDue === 0
                                ? 'Vence hoje'
                                : `${daysUntilDue}d restantes`}
                          </span>
                        )}
                        {item.source_context?.stage_name && (
                          <span className="truncate">{item.source_context.stage_name}</span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
});

WorkTimelineView.displayName = 'WorkTimelineView';
